/** Game constants — defaults aligned with Draradech/NewtonWars where applicable. */

export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;

/** Battlefield is square (web requirement). Area ≈ original A=2e6 → side √2e6. */
export const BATTLEFIELD_SIZE = Math.sqrt(2e6); // ≈ 1414.21

export const DEFAULT_CONFIG = {
  maxPlayersHardCap: 8,
  minPlayersToStart: 2,
  maxPlayersDefault: 4,
  numPlanets: 16,
  numShots: 16,
  /** Energy regeneration per second (original: rate). */
  energyRate: 2.0,
  /** Energy cap (original: limit). */
  energyLimit: 200,
  /** Round duration in seconds (original: roundTime). */
  roundTimeSec: 300,
  /** Scoreboard duration in seconds (original: 20). */
  boardTimeSec: 5,
  playerSize: 4.0,
  /**
   * Soft void margin beyond battlefield.
   * Original uses 500; we use a larger value so typical slingshot returns
   * are not cut short while still bounding runaway missiles.
   * Lifetime: 20s (Impossible trickshot window), then maxlen destroy.
   */
  margin: 5000,
  /** 1500 ticks @ 60 Hz ≈ 25s flight, then maxlen destroy (planet-impact FX). */
  maxSegments: 1500,
  segmentSteps: 25,
  numDebrisParticles: 10,
  debrisSpeed: 3.0,
  velocityMax: 50,
  velocityMin: 0,
  /** Initial energy on (re)spawn. */
  initialEnergy: 20,
  /** Default shot velocity. */
  defaultVelocity: 10,
  /** Trail fade duration in seconds for normal shots. */
  trailFadeSec: 60,
  /** Debris / kill-explosion trails fade faster. */
  debrisTrailFadeSec: 5,
  /** Network: ticks between full state broadcasts (60/3 = 20 Hz). */
  broadcastEveryTicks: 3,
  /** Max fire commands accepted per player per second. */
  fireRateLimitPerSec: 8,
  /** Max WS messages per connection per second (in-room). */
  messageRateLimitPerSec: 40,
  /** Max WS messages per connection per second (global, including lobby). */
  globalMessageRateLimitPerSec: 30,
  /** New rooms a connection may create per minute. */
  createRoomPerMinute: 3,
  /** Join/reconnect attempts per connection per minute (limits code guessing). */
  joinAttemptPerMinute: 30,
  /** hello / session create attempts per connection per minute. */
  helloPerMinute: 20,
  /** Max WebSocket frame size (bytes). */
  maxWsPayloadBytes: 64 * 1024,
  /** Soft cap on concurrent rooms (create rejected when reached). */
  maxConcurrentRooms: 32,
  /** Soft cap on concurrent WebSocket connections. */
  maxWsConnections: 80,
  /** Disconnect grace for reconnect (ms). */
  reconnectGraceMs: 45_000,
  /** Empty playing rooms cleaned after all players disconnect (ms). */
  roomTtlMs: 3 * 60_000,
  /** Idle waiting room with no players cleaned after (ms). */
  emptyWaitingTtlMs: 10 * 60_000,
} as const;

export type GameDefaults = typeof DEFAULT_CONFIG;
